import { Heading, SegmentedTabs } from "@overmap-ai/blocks"
import { type ProcedureTypeFields, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import {
	selectAttachmentsOfProcedureTypeFields,
	selectLatestProcedureTypeFieldsOfProcedureType,
	selectProcedureTypeById,
	selectProcedureTypeFieldsOfProcedureType,
} from "@overmap-ai/core"
import { FieldSchema, type FieldSchemaConfig, FormBuilder, getImagesFromFields } from "@overmap-ai/forms"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FieldsRevisionsTimeline } from "../../components/FieldsRevisionsTimeline/FieldsRevisionsTimeline"
import { useDeserializeFieldSchemaConfig, useHydrateFieldsWithAttachments } from "../../components/Form/hooks"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/procedure-types/$procedureTypeId/fields")

export const ProcedureTypeSettingsFieldsView = memo(() => {
	const { procedureTypeId } = routeApi.useParams()

	const procedureType = useAppSelector(selectProcedureTypeById(procedureTypeId))

	if (!procedureType) {
		throw new Error(`expected procedure type to be defined, got ${procedureType}`)
	}

	const latestProcedureTypeFields = useAppSelector(selectLatestProcedureTypeFieldsOfProcedureType(procedureType.uuid))
	const procedureTypeFieldsRevisions = useShallowEqaulSelector(
		selectProcedureTypeFieldsOfProcedureType(procedureType.uuid),
	)
	const procedureTypeFieldsAttachments = useShallowEqaulSelector(
		selectAttachmentsOfProcedureTypeFields(latestProcedureTypeFields?.uuid ?? ""),
	)
	const navigate = useNavigate()

	const handleRevisionSelect = useCallback(
		(procedureTypeFields: Stored<ProcedureTypeFields>) => {
			void navigate({
				from: "/$organizationId/settings/procedure-types/$procedureTypeId/fields",
				to: "/$organizationId/settings/procedure-types/$procedureTypeId/revisions/$procedureTypeFieldsId",
				params: (previousParams) => ({ ...previousParams, procedureTypeFieldsId: procedureTypeFields.uuid }),
			})
		},
		[navigate],
	)

	const handleFormBuilderSave = useCallback(
		async (fields: FieldSchemaConfig) => {
			const images = await getImagesFromFields(fields)

			const [offlineProcedureTypeFields, _promise] = sdk.procedureTypeFields.add({
				procedure_type: procedureType.uuid,
				fields: FieldSchema.serializeConfig(fields),
			})

			if (Object.keys(images).length !== 0) {
				void sdk.procedureTypeFieldsAttachments.bulkAdd(
					Object.entries(images).map(([fieldId, file]) => ({
						fields_revision: offlineProcedureTypeFields.uuid,
						field_identifier: fieldId,
						file,
					})),
				)
			}
		},
		[procedureType.uuid],
	)

	const fieldSchemaConfig = useDeserializeFieldSchemaConfig(latestProcedureTypeFields?.fields ?? EMPTY_ARRAY)

	const fieldsWithImages = useHydrateFieldsWithAttachments(
		fieldSchemaConfig,
		procedureTypeFieldsAttachments,
		sdk.procedureTypeFieldsAttachments,
	)

	return (
		<ViewContainer>
			<section className="flex size-full flex-col gap-4">
				<Heading size="xl">Fields</Heading>

				<FormBuilder.Root initialFields={fieldsWithImages} onSave={handleFormBuilderSave}>
					<SegmentedTabs.Root defaultValue="edit" className="flex size-full flex-col gap-4 overflow-hidden">
						<SegmentedTabs.List className="flex" accentColor="base" size="sm" variant="surface">
							<SegmentedTabs.Trigger className="grow basis-1" value="edit">
								List
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="flow">
								Flow
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="preview">
								Preview
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="history">
								History
							</SegmentedTabs.Trigger>
						</SegmentedTabs.List>

						<SegmentedTabs.Content value="edit" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.ListBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="flow" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.FlowBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="preview" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.Preview />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="history" className="size-full overflow-y-auto overflow-x-clip">
							<FieldsRevisionsTimeline
								fieldsRevisions={procedureTypeFieldsRevisions}
								onRevisionSelect={handleRevisionSelect}
							/>
						</SegmentedTabs.Content>
					</SegmentedTabs.Root>
				</FormBuilder.Root>
			</section>
		</ViewContainer>
	)
})
ProcedureTypeSettingsFieldsView.displayName = "ProcedureTypeSettingsFieldsView"
